
import { Component, Vue, Watch } from 'vue-property-decorator';
import reportsConfig from '@/config/reports-library';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import {
  getFeatureVersion,
  reportLibrarySwitchTarget,
  setFeatureVersion,
} from '@/config/atlasFeatures';

interface ReportItem {
  id: string;
  name: string;
  category: string;
  description: string;
  link: string;
}

interface CategoryWithReports {
  name: string;
  reports: ReportItem[];
  reportCount: number;
}

@Component({
  name: 'ReportLibrary',
  components: {
    TryNewAtlasBanner,
    CopyLinkButton,
  },
})
export default class ReportLibrary extends Vue {
  searchTerm = '';
  reports: ReportItem[] = reportsConfig.reports;
  filteredReports: ReportItem[] = reportsConfig.reports;
  categories = reportsConfig.categories;
  filterPanelExpanded: number | null = null;

  get categoriesWithReports(): Record<string, CategoryWithReports> {
    const result: Record<string, CategoryWithReports> = {};

    // Loop through all categories and check if they have reports
    Object.entries(this.categories).forEach(([categoryKey, categoryInfo]) => {
      const categoryReports = this.filteredReports.filter(report => report.category === categoryKey);

      // Only include categories that have reports
      if (categoryReports.length > 0) {
        result[categoryKey] = {
          name: categoryInfo.name,
          reports: categoryReports,
          reportCount: categoryReports.length
        };
      }
    });

    return result;
  }

  filterItems(updateUrl = true): void {
    const term = this.searchTerm?.toLowerCase() || '';

    if (term) {
      this.filteredReports = this.reports.filter(report =>
        report.name.toLowerCase().includes(term) ||
        report.description.toLowerCase().includes(term)
      );
    } else {
      this.filteredReports = this.reports;
    }

    // Update URL with search parameter only when triggered by user input
    if (updateUrl) {
      this.updateUrlWithSearch();
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filterItems();
  }

  updateUrlWithSearch(): void {
    const currentQuery = { ...this.$route.query };

    if (this.searchTerm) {
      currentQuery.search = this.searchTerm;
    } else {
      delete currentQuery.search;
    }

    // Only update if query has changed to avoid unnecessary navigation
    if (JSON.stringify(currentQuery) !== JSON.stringify(this.$route.query)) {
      this.$router.replace({ query: currentQuery }).catch(() => {
        // Ignore navigation duplicated error
      });
    }
  }

  @Watch('$route.query.search')
  onSearchQueryChange(newSearch: string | undefined): void {
    // Update searchTerm when URL parameter changes
    this.searchTerm = newSearch || '';
    // Don't update URL when filtering from URL change
    this.filterItems(false);
  }

  navigateToReport(item: ReportItem): void {
    if (item.link.startsWith('http')) {
      window.open(item.link, '_blank');
    } else {
      this.$router.push(item.link);
    }
  }

  mounted(): void {
    // Initialize search from URL parameter
    const searchParam = this.$route.query.search;
    if (searchParam && typeof searchParam === 'string') {
      this.searchTerm = searchParam;
    }
    this.filterItems();
  }

  /**
   * AS-10942: opt in to the atlas version of this page.
   *
   * Shares the `reports` flag with My Reports, so one click moves the whole browsing
   * set — but this page carries its own banner regardless. A merchant sitting on the
   * Report Library should not have to detour via My Reports to find the way forward;
   * the shared flag is an implementation detail they cannot see.
   */
  get showTryNewAtlasBanner(): boolean {
    return getFeatureVersion('reports') === 'classic';
  }

  public switchReportLibraryToAtlas(): void {
    setFeatureVersion('reports', 'atlas');
    // Single-sourced from ATLAS_FEATURE_LINKS so the forward switch and the
    // reverse redirect cannot drift apart.
    this.$router.push(reportLibrarySwitchTarget());
  }
}
